import {MarkerFeature} from "../models/MarkerFeature";
import {LineStringFeature} from "../models/LineStringFeature";
import {getElevationData} from "./HiikerElevationApiHelper";
import {length as turfLength} from "@turf/turf";
import toGeoJson from "@mapbox/togeojson";
import {GpxImportError, GpxImportErrorCode, isMalformedXml} from "./gpxImportError";

/**
 * A standalone point of interest carried in an imported file — a GPX <wpt>,
 * a KML <Placemark> point, or a GeoJSON Point feature. These are kept separate
 * from the route's start/end control markers so they can become hike highlights.
 */
export type ImportedWaypoint = {
  name?: string;
  description?: string;
  type?: string;
  lng: number;
  lat: number;
};

/**
 * Pull every standalone Point feature out of a parsed GeoJSON FeatureCollection.
 * GPX <wpt> and KML point Placemarks are converted to Point features by togeojson,
 * so this captures all three import formats. Line/track geometry is ignored here —
 * routes are handled separately.
 */
export function extractWaypointsFromGeoJson(geoJson: any): ImportedWaypoint[] {
  if (!geoJson || !Array.isArray(geoJson.features)) {
    return [];
  }
  return geoJson.features
    .filter((feature: any) =>
      feature && feature.geometry && feature.geometry.type === "Point" &&
      Array.isArray(feature.geometry.coordinates)
    )
    .map((feature: any) => {
      const props = feature.properties || {};
      const [lng, lat] = feature.geometry.coordinates;
      return {
        name: props.name ?? undefined,
        description: props.desc ?? props.cmt ?? props.description ?? "",
        type: "other",
        lng,
        lat,
      } as ImportedWaypoint;
    })
    .filter((wp: ImportedWaypoint) => Number.isFinite(wp.lng) && Number.isFinite(wp.lat));
}

function isGPX(fileContent) {
  const kmlStartTagRegex = /<gpx[^>]*>/i;
  const kmlEndTagRegex = /<\/gpx>/i;
  return kmlStartTagRegex.test(fileContent) && kmlEndTagRegex.test(fileContent);
}

function isKML(fileContent) {
  const kmlStartTagRegex = /<kml[^>]*>/i;
  const kmlEndTagRegex = /<\/kml>/i;
  return kmlStartTagRegex.test(fileContent) && kmlEndTagRegex.test(fileContent);
}

function isTCX(fileContent) {
  const tcxStartTagRegex = /<TrainingCenterDatabase[^>]*>/i;
  const tcxEndTagRegex = /<\/TrainingCenterDatabase>/i;
  return tcxStartTagRegex.test(fileContent) && tcxEndTagRegex.test(fileContent);
}


function isGeoJSON(fileContent) {
  try {
    const geoJSONData = JSON.parse(fileContent);
    return geoJSONData.type !== undefined;
  } catch (e) {
    return false;
  }
}

function readFileContent(file: File) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => {
      // @ts-ignore
      resolve(event.target.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsText(file);
  });
}

export async function getMarkersAndLineStringsFrom(file: File): Promise<MarkersAndLineStrings> {
  const routes = await getMarkersAndLineStringsListFrom(file);
  if (routes.length > 1) {
    throw MultiRouteError.fromLineStrings(getPrimaryLineStrings(routes));
  }

  if (routes.length === 1) {
    return routes[0];
  }

  throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
}

function getPrimaryLineStrings(routes: MarkersAndLineStrings[]): LineStringFeature[] {
  return routes
    .map((route) => route.lineStrings[0])
    .filter((lineString): lineString is LineStringFeature => !!lineString);
}

export async function getMarkersAndLineStringsListFrom(file: File): Promise<MarkersAndLineStrings[]> {
  let fileContent: string;
  try {
    fileContent = String(await readFileContent(file));
  } catch (e) {
    throw new GpxImportError(GpxImportErrorCode.Unreadable);
  }

  if (fileContent.trim() === '') {
    throw new GpxImportError(GpxImportErrorCode.Empty);
  }

  if (isGPX(fileContent)) {
    // Import as GPX
    return MarkersAndLineStrings.listFromGPX(fileContent);
  } else if (isKML(fileContent)) {
    // Import as KML
    return MarkersAndLineStrings.listFromKML(fileContent);
  } else if (isGeoJSON(fileContent)) {
    // Import as GeoJSON
    return MarkersAndLineStrings.listFromGeoJson(fileContent);
  } else if (isTCX(fileContent)) {
    // Import as TCX
    return MarkersAndLineStrings.listFromTCX(fileContent);
  }
  throw new GpxImportError(GpxImportErrorCode.UnsupportedType);
}

class MarkersAndLineStrings {

  markers: MarkerFeature[]
  lineStrings: LineStringFeature[]
  waypoints: ImportedWaypoint[]

  constructor(markers, lineStrings, waypoints: ImportedWaypoint[] = []) {
    this.markers = markers;
    this.lineStrings = lineStrings;
    this.waypoints = waypoints;
  }

  addMarker(marker) {
    this.markers.push(marker);
  }

  addLineString(lineString) {
    this.lineStrings.push(lineString);
  }

  static async fromGPX(gpxString): Promise<MarkersAndLineStrings> {
    const routes = await MarkersAndLineStrings.listFromGPX(gpxString);
    return MarkersAndLineStrings.ensureSingleRoute(routes);
  }

  static async listFromGPX(gpxString): Promise<MarkersAndLineStrings[]> {
    // A damaged GPX still matches the <gpx>…</gpx> sniff but parses to zero features,
    // which would otherwise look like "no routes". Detect the broken XML explicitly.
    if (isMalformedXml(gpxString)) {
      throw new GpxImportError(GpxImportErrorCode.MalformedXml);
    }

    let dom = new DOMParser().parseFromString(gpxString, 'text/xml');
    let geojson = toGeoJson.gpx(dom);

    return MarkersAndLineStrings.listFromGeoJson(JSON.stringify(geojson));
  }

  static async fromKML(kmlString): Promise<MarkersAndLineStrings> {
    const routes = await MarkersAndLineStrings.listFromKML(kmlString);
    return MarkersAndLineStrings.ensureSingleRoute(routes);
  }

  static async listFromKML(kmlString): Promise<MarkersAndLineStrings[]> {
    const xml = new DOMParser().parseFromString(kmlString, 'text/xml')
    const geoJson = toGeoJson.kml(xml)
    return MarkersAndLineStrings.listFromGeoJson(JSON.stringify(geoJson))
  }

  static async fromTCX(tcxString): Promise<MarkersAndLineStrings> {
    const routes = await MarkersAndLineStrings.listFromTCX(tcxString);
    return MarkersAndLineStrings.ensureSingleRoute(routes);
  }

  static async listFromTCX(tcxString): Promise<MarkersAndLineStrings[]> {
    const parser = new DOMParser();
    const tcxDoc = parser.parseFromString(tcxString, 'application/xml');
    const trackpoints = Array.from(tcxDoc.querySelectorAll('Trackpoint'));
    const coordinates = trackpoints.map(trackpoint => {
      const lat = parseFloat(trackpoint.querySelector('LatitudeDegrees').textContent);
      const lon = parseFloat(trackpoint.querySelector('LongitudeDegrees').textContent);
      const elevationElement = trackpoint.querySelector('AltitudeMeters');
      const elevation = elevationElement && elevationElement.textContent.trim() !== '' ?
        parseFloat(elevationElement.textContent) : 0.0;
      return [lon, lat, elevation];
    });

    const geojson = {
      "type": "FeatureCollection",
      "features": [{
        type: 'Feature',
        geometry: {
          type: 'LineString',
          coordinates: coordinates
        },
        properties: {
          name: tcxDoc.querySelector('Name')?.textContent ?? 'no-name'
        }
      }]
    };

    return MarkersAndLineStrings.listFromGeoJson(JSON.stringify(geojson))
  }

  static async fromGeoJson(geoJsonString): Promise<MarkersAndLineStrings> {
    const routes = await MarkersAndLineStrings.listFromGeoJson(geoJsonString);
    return MarkersAndLineStrings.ensureSingleRoute(routes);
  }

  static async listFromGeoJson(geoJsonString): Promise<MarkersAndLineStrings[]> {
    let geoJson = JSON.parse(geoJsonString)

    // Valid JSON that isn't a FeatureCollection has no routes to import.
    if (!geoJson || !Array.isArray(geoJson.features)) {
      throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
    }

    // Filter for both LineString and MultiLineString features
    let lineStringFeatures = geoJson.features.filter((feature) =>
      feature.geometry.type === "LineString" || feature.geometry.type === "MultiLineString"
    )

    // Convert features to normalized LineString coordinates
    const normalizedFeatures = await Promise.all(lineStringFeatures.map(async (feature) => {
      let coords;

      // Handle MultiLineString by flattening all segments into a single LineString
      if (feature.geometry.type === "MultiLineString") {
        // Flatten the array of line segments into a single coordinate array
        coords = feature.geometry.coordinates.flat()
      } else {
        coords = feature.geometry.coordinates
      }

      if (lineStringFeatures.length > 6) {
        return LineStringFeature.coordsOnlyFeature(coords)
      } else {
        let coordsWithUpdatedElevation = await getElevationData(coords.map(c => [c[1], c[0]]))
        // @ts-ignore
        return LineStringFeature.coordsOnlyFeature(coordsWithUpdatedElevation.map(h => [h.lng, h.lat, h.elevation]))
      }
    }));

    if (normalizedFeatures.length === 0) {
      throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack)
    }

    const routes = await Promise.all(
      normalizedFeatures.map((lineStringFeature) =>
        MarkersAndLineStrings.createLineStringAndMakersFrom(lineStringFeature)
      )
    );

    // Standalone points (GPX <wpt>, KML placemarks, GeoJSON Points) are file-level,
    // not tied to a single track. Attach them to the first route so they survive the
    // single-route selection flow and can be imported as hike highlights.
    const waypoints = extractWaypointsFromGeoJson(geoJson);
    if (routes.length > 0 && waypoints.length > 0) {
      routes[0].waypoints = waypoints;
    }

    return routes;
  }

  static ensureSingleRoute(routes: MarkersAndLineStrings[]): MarkersAndLineStrings {
    if (routes.length > 1) {
      throw MultiRouteError.fromLineStrings(getPrimaryLineStrings(routes));
    }

    if (routes.length === 1) {
      return routes[0];
    }

    throw new GpxImportError(GpxImportErrorCode.NoRouteOrTrack);
  }

  static async createLineStringAndMakersFrom(lineStringFeature: LineStringFeature) {
    let coords = lineStringFeature.lineStringGeometry.coordinates
    let coordsWithUpdatedElevation = await getElevationData(coords.map(c => [c[1], c[0]]))

    // @ts-ignore
    let lineStringFeatureWithElevation = LineStringFeature.coordsOnlyFeature(coordsWithUpdatedElevation.map(h => [h.lng, h.lat, h.elevation]))
    // @ts-ignore
    let length = turfLength(lineStringFeatureWithElevation.toGeoJson(), { units: 'meters' })
    let intervalLength = Math.floor(length / 15)
    let startCoord = coords[0]
    let endCoord = coords[coords.length - 1]

    let markers: MarkerFeature[] = []
    markers.push(
      await MarkerFeature.fromCoordinates(
        startCoord, true
      )
    )
    markers.push(
      await MarkerFeature.fromCoordinates(
        endCoord, true
      )
    )

    return  new MarkersAndLineStrings(markers, [lineStringFeatureWithElevation])
  }
}

export class MultiRouteError extends Error {
  lineStrings: LineStringFeature[]

  constructor(message) {
    super(message);
    this.lineStrings = [];
  }

  static fromLineStrings(lineStrings: LineStringFeature[]) {
    let error = new MultiRouteError("File contains more than one track or route.")
    error.lineStrings = lineStrings;
    return error;
  }
}
